import type { Block, BlockSource } from './types'

/**
 * The single place the block invariants live:
 *
 *  - at most one block is open (`end === null`)
 *  - blocks never overlap
 *  - `end > start` for every closed block
 *  - gaps are legal, and mean untracked time
 *
 * Everything that mutates blocks goes through a `plan*` function here and comes
 * back as a list of writes. `blocks.ts` is the only place that turns those into
 * Firestore operations, and it commits each plan as one atomic batch — so a lost
 * connection can never persist the "close" without the "open".
 */

/**
 * The shortest block there is. Anything you build by hand — a drag on the
 * timeline, an edge, a split, a seam — has to leave at least this much on
 * either side of it, so nothing lands on the calendar too thin to see or to
 * grab hold of afterwards.
 *
 * It is deliberately not applied to what the clock produces. A recording that
 * really did last forty seconds lasted forty seconds; rounding it up would push
 * the block after it into the future, which is a worse lie than a short block.
 */
export const MIN_BLOCK_MS = 60_000

/**
 * How long a tap stays a mistap. Within this, tapping a different category
 * corrects the running block in place rather than leaving a sliver behind it,
 * and re-tapping the lit one is a double-tap rather than a change of mind.
 *
 * Separate from the minimum length on purpose: this is a claim about how fast
 * two taps were, not about how short a block may be.
 */
export const MISTAP_MS = 1000

export type BlockPatch = Partial<Pick<Block, 'start' | 'end' | 'categoryId' | 'note' | 'tags'>>

export type NewBlock = Omit<Block, 'id'> & { id: string }

export type BlockWrite =
  | { kind: 'create'; block: NewBlock }
  | { kind: 'patch'; id: string; patch: BlockPatch }
  | { kind: 'delete'; id: string }

export type PlanErrorCode =
  | 'overlaps-existing'
  | 'starts-before-current-block'
  | 'end-before-start'
  | 'split-outside-block'
  | 'no-open-block'
  | 'unknown-block'

export interface PlanError {
  ok: false
  code: PlanErrorCode
  message: string
}

export interface PlanOk {
  ok: true
  writes: BlockWrite[]
}

export type Plan = PlanOk | PlanError

function fail(code: PlanErrorCode, message: string): PlanError {
  return { ok: false, code, message }
}

const ok = (writes: BlockWrite[]): PlanOk => ({ ok: true, writes })

export interface SwitchInput {
  /** The currently running block, or null when time is untracked. */
  open: Block | null
  /** Boundary instant: `Date.now()` for a tap, earlier for a backfill. */
  at: number
  categoryId: string
  /** Id for the block being created — generated by the caller so writes stay pure. */
  newId: string
  source: BlockSource
  note?: string | null
  tags?: string[]
  /**
   * Everything already logged around `at`. Needed because time can be logged
   * ahead of the present: a closed block can straddle the instant a new
   * recording starts, and starting on top of it would overlap.
   */
  blocks?: Block[]
}

/**
 * Close the running block at `at` and open a new one at the same instant, so the
 * two share a boundary and no gap or overlap can appear between them.
 */
export function planSwitch(input: SwitchInput): Plan {
  const { open, at, categoryId, newId, source } = input
  const note = input.note ?? null
  const tags = input.tags ?? []

  // Time logged ahead of the present is legitimate — you can fill in the rest of
  // an afternoon and then have it turn out differently. A closed block straddling
  // `at` is cut there, so the recording that starts now takes over from it rather
  // than being laid on top of it.
  const straddling = (input.blocks ?? []).find(
    (block) =>
      block.id !== open?.id &&
      block.end !== null &&
      block.start < at &&
      block.end > at &&
      at - block.start >= MISTAP_MS,
  )
  const cut: BlockWrite[] = straddling
    ? [{ kind: 'patch', id: straddling.id, patch: { end: at } }]
    : []

  if (!open) {
    return ok([
      ...cut,
      { kind: 'create', block: { id: newId, start: at, end: null, categoryId, note, tags, source } },
    ])
  }

  if (at < open.start) {
    return fail(
      'starts-before-current-block',
      'That start time is before the block currently running. Edit the earlier blocks instead.',
    )
  }

  // A mistap, corrected in place: no sliver block, no lost boundary.
  if (at - open.start < MISTAP_MS) {
    if (open.categoryId === categoryId) return ok([])
    return ok([{ kind: 'patch', id: open.id, patch: { categoryId, note, tags } }])
  }

  // Re-tapping the running category is a no-op unless the sheet supplied
  // something to record alongside it.
  if (open.categoryId === categoryId && note === null && tags.length === 0) {
    return ok([])
  }

  return ok([
    ...cut,
    { kind: 'patch', id: open.id, patch: { end: at } },
    { kind: 'create', block: { id: newId, start: at, end: null, categoryId, note, tags, source } },
  ])
}

/** Stop tracking entirely — closes the running block and opens nothing. */
export function planStop(open: Block | null, at: number): Plan {
  if (!open) return fail('no-open-block', 'Nothing is running.')
  if (at <= open.start) {
    return fail('end-before-start', 'An end time has to be after the block started.')
  }
  return ok([{ kind: 'patch', id: open.id, patch: { end: at } }])
}

/**
 * Split a block in two at `at`. Used by the stale banner ("I left steer-ai running
 * through lunch") and by the timeline editor.
 */
export function planSplit(block: Block, at: number, newId: string, categoryId?: string): Plan {
  const upperBound = block.end ?? Number.POSITIVE_INFINITY
  if (at - block.start < MIN_BLOCK_MS || upperBound - at < MIN_BLOCK_MS) {
    return fail('split-outside-block', 'A split has to leave at least a minute on each side.')
  }
  return ok([
    { kind: 'patch', id: block.id, patch: { end: at } },
    {
      kind: 'create',
      block: {
        id: newId,
        start: at,
        end: block.end,
        categoryId: categoryId ?? block.categoryId,
        note: block.note,
        tags: block.tags,
        source: 'split',
      },
    },
  ])
}

/** Edit a block's own bounds, refusing anything that would collide with its neighbours. */
export function planResize(
  block: Block,
  bounds: { start?: number; end?: number | null },
  neighbours: { previous: Block | null; next: Block | null },
): Plan {
  const start = bounds.start ?? block.start
  const end = bounds.end === undefined ? block.end : bounds.end
  if (end !== null && end - start < MIN_BLOCK_MS) {
    return fail('end-before-start', 'A block has to be at least a minute long.')
  }
  const { previous, next } = neighbours
  if (previous && previous.end !== null && start < previous.end) {
    return fail('starts-before-current-block', 'That would overlap the previous block.')
  }
  if (next && end !== null && end > next.start) {
    return fail('end-before-start', 'That would overlap the next block.')
  }
  const patch: BlockPatch = {}
  if (start !== block.start) patch.start = start
  if (end !== block.end) patch.end = end
  return ok(Object.keys(patch).length === 0 ? [] : [{ kind: 'patch', id: block.id, patch }])
}

/**
 * Create a closed block in a free stretch of the timeline — the drag-to-paint
 * gesture on Today and Week. Refuses anything that would land on top of an
 * existing block; the caller is expected to have clamped the drag to a gap.
 */
export function planCreate(
  blocks: Block[],
  draft: { start: number; end: number; categoryId: string; newId: string; source?: BlockSource } & Partial<
    Pick<Block, 'note' | 'tags'>
  >,
): Plan {
  const { start, end, categoryId, newId } = draft
  if (end - start < MIN_BLOCK_MS) {
    return fail('end-before-start', 'That range is under a minute — too short to be a block.')
  }
  for (const block of blocks) {
    const blockEnd = block.end ?? Number.POSITIVE_INFINITY
    if (start < blockEnd && end > block.start) {
      return fail('overlaps-existing', 'That range overlaps a block that is already there.')
    }
  }
  return ok([
    {
      kind: 'create',
      block: {
        id: newId,
        start,
        end,
        categoryId,
        note: draft.note ?? null,
        tags: draft.tags ?? [],
        source: draft.source ?? 'manual',
      },
    },
  ])
}

/** Remove a block outright. The time it covered becomes an untracked gap. */
export function planDelete(block: Block): Plan {
  return ok([{ kind: 'delete', id: block.id }])
}

/** Change a block's category, note or tags without touching its bounds. */
export function planEditMeta(
  block: Block,
  patch: Partial<Pick<Block, 'categoryId' | 'note' | 'tags'>>,
): Plan {
  const changed: BlockPatch = {}
  for (const key of Object.keys(patch) as (keyof typeof patch)[]) {
    const value = patch[key]
    if (value === undefined) continue
    if (key === 'tags') {
      const next = value as string[]
      if (next.length !== block.tags.length || next.some((tag, i) => tag !== block.tags[i])) {
        changed.tags = next
      }
      continue
    }
    if (value !== block[key]) Object.assign(changed, { [key]: value })
  }
  return ok(Object.keys(changed).length === 0 ? [] : [{ kind: 'patch', id: block.id, patch: changed }])
}

/**
 * The block editor's save: category, note, tags and new bounds together. Both
 * halves patch the same block, so they are merged into one write — a retimed
 * block can never land with its new times and its old category.
 */
export function planEdit(
  block: Block,
  edit: Partial<Pick<Block, 'categoryId' | 'note' | 'tags'>> & {
    bounds?: { start: number; end: number | null }
  },
  neighbours: { previous: Block | null; next: Block | null },
): Plan {
  const { bounds: nextBounds, ...rest } = edit
  const meta = planEditMeta(block, rest)
  if (!meta.ok || !nextBounds) return meta

  const bounds = planResize(block, nextBounds, neighbours)
  if (!bounds.ok) return bounds

  const patch: BlockPatch = {}
  for (const write of [...meta.writes, ...bounds.writes]) {
    if (write.kind === 'patch') Object.assign(patch, write.patch)
  }
  return ok(Object.keys(patch).length === 0 ? [] : [{ kind: 'patch', id: block.id, patch }])
}

/**
 * Move the boundary two adjacent blocks share: one shrinks by exactly what the
 * other grows. Dragging the seam between "impl" and "rework" is one gesture, not
 * a shrink followed by a stretch, so no gap can open up in between and neither
 * side can be left overlapping the other.
 */
export function planMoveBoundary(previous: Block, next: Block, at: number): Plan {
  // Pressing a seam without dragging it is not a change, and should not cost two
  // writes to say so.
  if (at === next.start && at === previous.end) return ok([])
  if (at - previous.start < MIN_BLOCK_MS) {
    return fail('end-before-start', 'That would leave the earlier block under a minute.')
  }
  const upperBound = next.end ?? Number.POSITIVE_INFINITY
  if (upperBound - at < MIN_BLOCK_MS) {
    return fail('end-before-start', 'That would leave the later block under a minute.')
  }
  return ok([
    { kind: 'patch', id: previous.id, patch: { end: at } },
    { kind: 'patch', id: next.id, patch: { start: at } },
  ])
}

/**
 * Shift a block in time without changing how long it is.
 *
 * Checked against every block rather than the two it started between, because a
 * drag can carry it clean over a neighbour into a gap on the far side — so
 * "the previous block" is not the same one it was when the gesture began.
 */
export function planMove(block: Block, delta: number, blocks: Block[]): Plan {
  if (delta === 0) return ok([])
  const start = block.start + delta
  const end = block.end === null ? null : block.end + delta
  // A running block has no end to collide with; it only has to land somewhere free.
  const upper = end ?? start + MIN_BLOCK_MS

  for (const other of blocks) {
    if (other.id === block.id) continue
    const otherEnd = other.end ?? Number.POSITIVE_INFINITY
    if (start < otherEnd && upper > other.start) {
      return fail('overlaps-existing', 'That would land on a block already there.')
    }
  }

  const patch: BlockPatch = { start }
  if (end !== null) patch.end = end
  return ok([{ kind: 'patch', id: block.id, patch }])
}

/** Reverse a plan against the state it was computed from — the Undo snackbar. */
export function planUndo(before: Block[], writes: BlockWrite[]): Plan {
  const byId = new Map(before.map((block) => [block.id, block]))
  const reverted: BlockWrite[] = []
  for (const write of writes) {
    if (write.kind === 'create') {
      reverted.push({ kind: 'delete', id: write.block.id })
      continue
    }
    const original = byId.get(write.id)
    if (!original) return fail('unknown-block', 'That change can no longer be undone.')
    if (write.kind === 'delete') {
      reverted.push({ kind: 'create', block: { ...original } })
      continue
    }
    const patch: BlockPatch = {}
    for (const key of Object.keys(write.patch) as (keyof BlockPatch)[]) {
      Object.assign(patch, { [key]: original[key] })
    }
    reverted.push({ kind: 'patch', id: write.id, patch })
  }
  return ok(reverted.reverse())
}

/** In-memory application of a plan — used by the optimistic UI and by the tests. */
export function applyWrites(blocks: Block[], writes: BlockWrite[]): Block[] {
  const next = new Map(blocks.map((block) => [block.id, { ...block }]))
  for (const write of writes) {
    if (write.kind === 'create') {
      next.set(write.block.id, { ...write.block })
    } else if (write.kind === 'delete') {
      next.delete(write.id)
    } else {
      const existing = next.get(write.id)
      if (existing) next.set(write.id, { ...existing, ...write.patch })
    }
  }
  return [...next.values()].sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))
}

export type InvariantViolation =
  | { kind: 'multiple-open'; ids: string[] }
  | { kind: 'overlap'; ids: [string, string] }
  | { kind: 'non-positive-duration'; id: string }
  | { kind: 'duplicate-id'; id: string }
  /** A block that started before now sits after the open one, so both claim the same time. */
  | { kind: 'open-block-overtaken'; id: string }

/**
 * Checks the whole set. Gaps are deliberately not violations, and neither is a
 * block logged after the open one — you can fill in the evening and keep
 * recording. What is a violation is the open block being *overtaken*: a block
 * that has already started sitting after it, so both claim the present.
 */
export function findInvariantViolations(
  blocks: Block[],
  now: number = Date.now(),
): InvariantViolation[] {
  const violations: InvariantViolation[] = []
  const seen = new Set<string>()
  for (const block of blocks) {
    if (seen.has(block.id)) violations.push({ kind: 'duplicate-id', id: block.id })
    seen.add(block.id)
  }

  const open = blocks.filter((block) => block.end === null)
  if (open.length > 1) {
    violations.push({ kind: 'multiple-open', ids: open.map((block) => block.id) })
  }

  const sorted = [...blocks].sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))
  for (const block of sorted) {
    if (block.end !== null && block.end <= block.start) {
      violations.push({ kind: 'non-positive-duration', id: block.id })
    }
  }
  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i] as Block
    const following = sorted[i + 1] as Block
    if (current.end === null) {
      if (following.start < now) violations.push({ kind: 'open-block-overtaken', id: current.id })
      continue
    }
    if (current.end > following.start) {
      violations.push({ kind: 'overlap', ids: [current.id, following.id] })
    }
  }
  return violations
}

export function assertInvariants(blocks: Block[], now?: number): void {
  const violations = findInvariantViolations(blocks, now)
  if (violations.length > 0) {
    throw new Error(`Block invariants violated: ${JSON.stringify(violations)}`)
  }
}
